import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingBag, X, ChevronLeft, ChevronRight, Star, Package, Truck, Shield, Minus, Plus, ExternalLink, Clock, CheckCircle, CircleDot, XCircle, ThumbsUp, Camera, Trash2, Play } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

// Category config with emoji + gradient for placeholder cards
const CATEGORY_CONFIG = {
    all: { label: 'All', emoji: '🛍️' },
    mugs: { label: 'Mugs', emoji: '☕', gradient: 'from-amber-600 to-orange-500' },
    tshirts: { label: 'T-Shirts', emoji: '👕', gradient: 'from-purple-600 to-indigo-500' },
    mousepads: { label: 'Mousepads', emoji: '🖱️', gradient: 'from-cyan-600 to-blue-500' },
    posters: { label: 'Posters', emoji: '🖼️', gradient: 'from-rose-600 to-pink-500' },
    collectibles: { label: 'Collectibles', emoji: '✨', gradient: 'from-yellow-600 to-amber-500' },
};

// Product placeholder image component
const ProductImage = ({ product, size = 'card' }) => {
    const cat = CATEGORY_CONFIG[product.category] || CATEGORY_CONFIG.all;
    const isCard = size === 'card';

    // If product has a real image, try to load it — prefer mockup over original artwork
    const displayImage = product.mockup_url || product.image_url;
    if (displayImage && !displayImage.startsWith('/store/')) {
        return (
            <div className={`relative overflow-hidden ${isCard ? 'h-56' : 'h-80'} bg-gray-800`}>
                <img
                    src={displayImage}
                    alt={product.name}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                />
                <div className={`hidden items-center justify-center w-full h-full bg-gradient-to-br ${cat.gradient || 'from-gray-700 to-gray-600'}`}>
                    <span className={isCard ? 'text-6xl' : 'text-8xl'}>{cat.emoji}</span>
                </div>
            </div>
        );
    }

    // Use gradient placeholder
    return (
        <div className={`relative overflow-hidden ${isCard ? 'h-56' : 'h-80'} bg-gradient-to-br ${cat.gradient || 'from-gray-700 to-gray-600'} flex items-center justify-center`}>
            <div className="text-center">
                <span className={isCard ? 'text-6xl' : 'text-8xl'}>{cat.emoji}</span>
                <p className="text-white/60 text-xs mt-2 font-medium uppercase tracking-wider">{cat.label}</p>
            </div>
            {/* Decorative pattern */}
            <div className="absolute inset-0 opacity-10" style={{
                backgroundImage: 'radial-gradient(circle at 25% 25%, white 1px, transparent 1px), radial-gradient(circle at 75% 75%, white 1px, transparent 1px)',
                backgroundSize: '24px 24px'
            }} />
        </div>
    );
};

// ═══ Star Rating Display ═══
const StarRating = ({ rating, size = 'sm', showValue = false, count = null }) => {
    const sizes = { xs: 'w-3 h-3', sm: 'w-4 h-4', md: 'w-5 h-5', lg: 'w-6 h-6' };
    const sz = sizes[size] || sizes.sm;
    return (
        <div className="flex items-center gap-1">
            <div className="flex">
                {[1, 2, 3, 4, 5].map(i => (
                    <Star key={i} className={`${sz} ${i <= Math.round(rating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-600'}`} />
                ))}
            </div>
            {showValue && <span className="text-sm font-bold text-white ml-1">{rating}</span>}
            {count !== null && <span className="text-xs text-gray-500 ml-1">({count})</span>}
        </div>
    );
};

// ═══ Reviews Section ═══
const ReviewsSection = ({ productId }) => {
    const [reviewData, setReviewData] = useState({ average_rating: 0, total_reviews: 0, distribution: {}, reviews: [] });
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [newRating, setNewRating] = useState(0);
    const [hoverRating, setHoverRating] = useState(0);
    const [newTitle, setNewTitle] = useState('');
    const [newBody, setNewBody] = useState('');
    const [mediaFiles, setMediaFiles] = useState([]);
    const [mediaPreviews, setMediaPreviews] = useState([]);
    const [lightboxMedia, setLightboxMedia] = useState(null);
    const [submitError, setSubmitError] = useState('');
    const fileInputRef = useRef(null);
    const token = localStorage.getItem('token');

    const fetchReviews = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/api/store/products/${productId}/reviews`);
            setReviewData(res.data);
        } catch (err) {
            console.error('Fetch reviews error:', err);
        } finally {
            setLoading(false);
        }
    }, [productId]);

    useEffect(() => { fetchReviews(); }, [fetchReviews]);

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files || []);
        addFiles(files);
    };

    const addFiles = (files) => {
        const remaining = 5 - mediaFiles.length;
        const toAdd = files.slice(0, remaining);
        const newFiles = [];
        const newPreviews = [];
        toAdd.forEach(file => {
            const isVideo = file.type.startsWith('video') || file.type === 'image/gif';
            const maxSize = isVideo ? 10 * 1024 * 1024 : 5 * 1024 * 1024;
            if (file.size > maxSize) return;
            newFiles.push(file);
            newPreviews.push({
                url: URL.createObjectURL(file),
                type: isVideo ? 'video' : 'image',
                name: file.name
            });
        });
        setMediaFiles(prev => [...prev, ...newFiles]);
        setMediaPreviews(prev => [...prev, ...newPreviews]);
    };

    const removeFile = (idx) => {
        URL.revokeObjectURL(mediaPreviews[idx]?.url);
        setMediaFiles(prev => prev.filter((_, i) => i !== idx));
        setMediaPreviews(prev => prev.filter((_, i) => i !== idx));
    };

    const handleSubmit = async () => {
        if (!token) { setSubmitError('Please log in to submit a review.'); return; }
        if (newRating === 0) { setSubmitError('Please select a star rating.'); return; }
        if (!newBody.trim() || newBody.trim().length < 5) { setSubmitError('Review must be at least 5 characters.'); return; }
        setSubmitError('');
        setSubmitting(true);
        try {
            const formData = new FormData();
            formData.append('rating', newRating);
            formData.append('body', newBody.trim());
            if (newTitle.trim()) formData.append('title', newTitle.trim());
            mediaFiles.forEach(f => formData.append('media', f));

            await axios.post(`${API}/api/store/products/${productId}/reviews`, formData, {
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
            });
            // Reset form
            setNewRating(0); setNewTitle(''); setNewBody(''); setMediaFiles([]); setMediaPreviews([]); setShowForm(false);
            await fetchReviews();
        } catch (err) {
            setSubmitError(err.response?.data?.detail || 'Failed to submit review.');
        } finally {
            setSubmitting(false);
        }
    };

    const toggleHelpful = async (reviewId) => {
        if (!token) return;
        try {
            const res = await axios.post(`${API}/api/store/reviews/${reviewId}/helpful`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setReviewData(prev => ({
                ...prev,
                reviews: prev.reviews.map(r =>
                    r.id === reviewId ? { ...r, helpful_count: res.data.helpful_count, helpful_by: res.data.action === 'added' ? [...(r.helpful_by || []), 'me'] : (r.helpful_by || []).filter(x => x !== 'me') } : r
                )
            }));
        } catch (err) { console.error('Helpful toggle error:', err); }
    };

    const deleteReview = async (reviewId) => {
        if (!window.confirm('Delete your review? This cannot be undone.')) return;
        try {
            await axios.delete(`${API}/api/store/reviews/${reviewId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            await fetchReviews();
        } catch (err) { console.error('Delete review error:', err); }
    };

    const { average_rating, total_reviews, distribution, reviews } = reviewData;
    const maxDist = Math.max(...Object.values(distribution || {}), 1);

    return (
        <div className="border-t border-gray-800 px-6 py-5">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                    Ratings & Reviews
                </h3>
                {token && (
                    <button
                        onClick={() => setShowForm(!showForm)}
                        className="text-xs font-semibold text-purple-400 hover:text-purple-300 bg-purple-500/10 px-3 py-1.5 rounded-lg hover:bg-purple-500/20 transition-all"
                    >
                        {showForm ? 'Cancel' : '✏️ Write a Review'}
                    </button>
                )}
            </div>

            {/* Rating Summary */}
            {total_reviews > 0 && (
                <div className="flex gap-6 mb-5 p-4 rounded-xl bg-gray-800/40">
                    {/* Big average */}
                    <div className="text-center min-w-[80px]">
                        <div className="text-4xl font-black text-white">{average_rating}</div>
                        <StarRating rating={average_rating} size="xs" />
                        <p className="text-[10px] text-gray-500 mt-1">{total_reviews} review{total_reviews !== 1 ? 's' : ''}</p>
                    </div>
                    {/* Distribution bars */}
                    <div className="flex-1 space-y-1">
                        {[5, 4, 3, 2, 1].map(star => {
                            const count = distribution[String(star)] || 0;
                            const pct = total_reviews > 0 ? Math.round((count / total_reviews) * 100) : 0;
                            return (
                                <div key={star} className="flex items-center gap-2 text-xs">
                                    <span className="text-gray-400 w-4 text-right">{star}</span>
                                    <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                                    <div className="flex-1 h-2 bg-gray-700 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-gradient-to-r from-yellow-400 to-amber-500 rounded-full transition-all duration-500"
                                            style={{ width: `${pct}%` }}
                                        />
                                    </div>
                                    <span className="text-gray-500 w-8 text-right">{pct}%</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Write Review Form */}
            {showForm && (
                <div className="mb-5 p-4 rounded-xl bg-gray-800/60 border border-gray-700/50 space-y-3">
                    {/* Star picker */}
                    <div>
                        <label className="text-xs text-gray-400 mb-1 block">Your Rating *</label>
                        <div className="flex gap-1">
                            {[1, 2, 3, 4, 5].map(i => (
                                <button
                                    key={i}
                                    onMouseEnter={() => setHoverRating(i)}
                                    onMouseLeave={() => setHoverRating(0)}
                                    onClick={() => setNewRating(i)}
                                    className="p-0.5 transition-transform hover:scale-125"
                                >
                                    <Star className={`w-7 h-7 transition-colors ${i <= (hoverRating || newRating)
                                            ? 'text-yellow-400 fill-yellow-400'
                                            : 'text-gray-600 hover:text-gray-500'
                                        }`} />
                                </button>
                            ))}
                            {newRating > 0 && <span className="text-sm text-gray-400 ml-2 self-center">{['', 'Terrible', 'Poor', 'Average', 'Good', 'Excellent'][newRating]}</span>}
                        </div>
                    </div>

                    {/* Title */}
                    <input
                        placeholder="Review title (optional)"
                        value={newTitle}
                        onChange={e => setNewTitle(e.target.value.slice(0, 100))}
                        className="w-full px-3 py-2 rounded-lg bg-gray-900 border border-gray-700 text-white text-sm focus:outline-none focus:border-purple-500 placeholder-gray-600"
                    />

                    {/* Body */}
                    <div>
                        <textarea
                            placeholder="Write your review... *"
                            value={newBody}
                            onChange={e => setNewBody(e.target.value.slice(0, 2000))}
                            rows={4}
                            className="w-full px-3 py-2 rounded-lg bg-gray-900 border border-gray-700 text-white text-sm focus:outline-none focus:border-purple-500 placeholder-gray-600 resize-none"
                        />
                        <p className="text-[10px] text-gray-600 text-right">{newBody.length}/2000</p>
                    </div>

                    {/* Media upload */}
                    <div>
                        <input ref={fileInputRef} type="file" multiple accept="image/*,video/*,.gif" onChange={handleFileSelect} className="hidden" />
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            disabled={mediaFiles.length >= 5}
                            className="flex items-center gap-2 text-xs text-cyan-400 hover:text-cyan-300 bg-cyan-500/10 px-3 py-2 rounded-lg hover:bg-cyan-500/15 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            <Camera className="w-4 h-4" />
                            Add Photos/Videos ({mediaFiles.length}/5)
                        </button>
                        {/* Previews */}
                        {mediaPreviews.length > 0 && (
                            <div className="flex gap-2 mt-2 flex-wrap">
                                {mediaPreviews.map((p, i) => (
                                    <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border border-gray-700 group">
                                        {p.type === 'video' ? (
                                            <video src={p.url} className="w-full h-full object-cover" muted />
                                        ) : (
                                            <img src={p.url} alt="" className="w-full h-full object-cover" />
                                        )}
                                        {p.type === 'video' && (
                                            <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                                                <Play className="w-4 h-4 text-white fill-white" />
                                            </div>
                                        )}
                                        <button
                                            onClick={() => removeFile(i)}
                                            className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                        >
                                            <X className="w-3 h-3 text-white" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {submitError && <p className="text-xs text-red-400">{submitError}</p>}

                    <button
                        onClick={handleSubmit}
                        disabled={submitting || newRating === 0 || newBody.trim().length < 5}
                        className="w-full py-2.5 rounded-lg font-semibold text-sm transition-all bg-gradient-to-r from-purple-600 to-cyan-500 text-white hover:from-purple-500 hover:to-cyan-400 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        {submitting ? '⏳ Submitting...' : 'Submit Review'}
                    </button>
                </div>
            )}

            {/* Reviews List */}
            {loading ? (
                <div className="space-y-3">
                    {[1, 2].map(i => (
                        <div key={i} className="p-4 rounded-xl bg-gray-800/30 animate-pulse">
                            <div className="flex gap-3">
                                <div className="w-8 h-8 bg-gray-700 rounded-full" />
                                <div className="flex-1 space-y-2">
                                    <div className="h-3 bg-gray-700 rounded w-1/3" />
                                    <div className="h-3 bg-gray-700 rounded w-full" />
                                    <div className="h-3 bg-gray-700 rounded w-2/3" />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : reviews.length === 0 ? (
                <div className="text-center py-8">
                    <Star className="w-10 h-10 text-gray-700 mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">No reviews yet. Be the first to review!</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {reviews.map(review => (
                        <div key={review.id} className="p-4 rounded-xl bg-gray-800/30 border border-gray-800/50 hover:border-gray-700/50 transition-all">
                            {/* Header: avatar + name + date */}
                            <div className="flex items-center gap-2.5 mb-2">
                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center text-white text-xs font-bold overflow-hidden">
                                    {review.avatar_url ? (
                                        <img src={review.avatar_url} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                        review.username?.[0]?.toUpperCase() || '?'
                                    )}
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-semibold text-white">{review.username}</span>
                                        {review.verified_purchase && (
                                            <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-green-400 bg-green-500/10 px-1.5 py-0.5 rounded-full">
                                                <CheckCircle className="w-3 h-3" /> Verified Purchase
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-[10px] text-gray-500">
                                        {review.created_at ? new Date(review.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                                    </p>
                                </div>
                            </div>

                            {/* Stars */}
                            <StarRating rating={review.rating} size="xs" />

                            {/* Title + Body */}
                            {review.title && <h4 className="text-sm font-bold text-white mt-2">{review.title}</h4>}
                            <p className="text-sm text-gray-300 mt-1 leading-relaxed whitespace-pre-wrap">{review.body}</p>

                            {/* Media gallery */}
                            {review.media && review.media.length > 0 && (
                                <div className="flex gap-2 mt-3 flex-wrap">
                                    {review.media.map((m, i) => (
                                        <button
                                            key={i}
                                            onClick={() => setLightboxMedia(m)}
                                            className="relative w-16 h-16 rounded-lg overflow-hidden border border-gray-700 hover:border-purple-500 transition-all hover:scale-105"
                                        >
                                            {m.type === 'video' ? (
                                                <>
                                                    <img src={m.thumbnail_url} alt="" className="w-full h-full object-cover" />
                                                    <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                                                        <Play className="w-4 h-4 text-white fill-white" />
                                                    </div>
                                                </>
                                            ) : (
                                                <img src={m.thumbnail_url || m.url} alt="" className="w-full h-full object-cover" />
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* Actions: helpful + delete */}
                            <div className="flex items-center gap-3 mt-3">
                                <button
                                    onClick={() => toggleHelpful(review.id)}
                                    className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg transition-all ${review.helpful_by?.includes('me')
                                            ? 'text-purple-400 bg-purple-500/15 font-semibold'
                                            : 'text-gray-500 hover:text-gray-300 hover:bg-gray-700/50'
                                        }`}
                                >
                                    <ThumbsUp className="w-3.5 h-3.5" />
                                    Helpful{review.helpful_count > 0 ? ` (${review.helpful_count})` : ''}
                                </button>
                                {token && review.user_id === JSON.parse(atob(token.split('.')[1]))?.sub && (
                                    <button
                                        onClick={() => deleteReview(review.id)}
                                        className="flex items-center gap-1 text-xs text-red-400/60 hover:text-red-400 transition-all"
                                    >
                                        <Trash2 className="w-3 h-3" /> Delete
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Media Lightbox */}
            {lightboxMedia && (
                <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4" onClick={() => setLightboxMedia(null)}>
                    <button onClick={() => setLightboxMedia(null)} className="absolute top-4 right-4 p-2 rounded-full bg-gray-800/80 text-white hover:bg-gray-700 z-10">
                        <X className="w-6 h-6" />
                    </button>
                    <div className="max-w-3xl max-h-[85vh]" onClick={e => e.stopPropagation()}>
                        {lightboxMedia.type === 'video' ? (
                            <video src={lightboxMedia.url} controls autoPlay className="max-w-full max-h-[85vh] rounded-xl" />
                        ) : (
                            <img src={lightboxMedia.url} alt="Review media" className="max-w-full max-h-[85vh] rounded-xl object-contain" />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// Product Detail Modal with Multi-Angle Gallery + Checkout
const ProductModal = ({ product, onClose }) => {
    const [selectedSize, setSelectedSize] = useState('');
    const [quantity, setQuantity] = useState(1);
    const [step, setStep] = useState('details'); // 'details' | 'shipping' | 'rates' | 'processing'
    const [processing, setProcessing] = useState(false);
    const [activeImageIndex, setActiveImageIndex] = useState(0);
    const [shippingOptions, setShippingOptions] = useState([]);
    const [selectedShipping, setSelectedShipping] = useState(null);
    const [loadingRates, setLoadingRates] = useState(false);
    const [shipping, setShipping] = useState({
        name: '', address1: '', address2: '', city: '',
        state_code: '', zip: '', phone: '', country_code: 'IN'
    });
    const sizeVariant = product.variants?.find(v => v.type === 'size');
    const token = localStorage.getItem('token');

    // Build all available images for the gallery
    const getAllImages = useCallback(() => {
        const imgs = [];
        const seen = new Set();
        const addImg = (url, label) => {
            if (url && !url.startsWith('/store/') && !seen.has(url)) {
                seen.add(url);
                imgs.push({ url, label });
            }
        };

        // 1. Main mockup (primary view)
        addImg(product.mockup_url, 'Product View');

        // 2. Additional images array (multi-angle mockups)
        if (Array.isArray(product.images)) {
            product.images.forEach((img, i) => {
                const url = typeof img === 'string' ? img : img?.mockup_url || img?.url;
                addImg(url, `View ${i + 1}`);
            });
        }

        // 3. Variant-specific images
        const variantImgs = product.variant_images || {};
        Object.entries(variantImgs).forEach(([variant, url]) => {
            addImg(url, `${variant} Design`);
        });

        // 4. Original design art as fallback
        addImg(product.image_url, 'Design Art');

        return imgs.length > 0 ? imgs : [{ url: '', label: 'No Image' }];
    }, [product]);

    const allImages = getAllImages();

    // When size/variant changes, try to jump to variant-specific image
    useEffect(() => {
        if (selectedSize && product.variant_images?.[selectedSize]) {
            const variantUrl = product.variant_images[selectedSize];
            const idx = allImages.findIndex(img => img.url === variantUrl);
            if (idx >= 0) setActiveImageIndex(idx);
        }
    }, [selectedSize]);

    useEffect(() => {
        if (sizeVariant?.options?.length > 0) {
            setSelectedSize(sizeVariant.options[1] || sizeVariant.options[0]); // Default to M
        }
    }, []);

    // Load Razorpay script
    useEffect(() => {
        if (!document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]')) {
            const s = document.createElement('script');
            s.src = 'https://checkout.razorpay.com/v1/checkout.js';
            s.async = true;
            document.head.appendChild(s);
        }
    }, []);

    const handleShippingChange = (field, value) => {
        setShipping(prev => ({ ...prev, [field]: value }));
    };

    const isShippingValid = shipping.name && shipping.address1 && shipping.city &&
        shipping.state_code && shipping.zip && shipping.phone;

    const fetchShippingRates = async () => {
        setLoadingRates(true);
        try {
            const res = await axios.post(`${API}/api/store/shipping-rates`, {
                product_type: product.category,
                variant_size: selectedSize,
                quantity,
                shipping_address: shipping
            });
            const options = res.data.shipping_options || [];
            setShippingOptions(options);
            if (options.length > 0) setSelectedShipping(options[0]);
            setStep('rates');
        } catch (err) {
            console.error('Shipping rates error:', err);
            alert('Failed to calculate shipping. Please try again.');
        } finally {
            setLoadingRates(false);
        }
    };

    const handleBuyNow = async () => {
        if (!token) {
            alert('Please log in to purchase.');
            return;
        }
        if (step === 'details') {
            setStep('shipping');
            return;
        }
        if (step === 'shipping') {
            if (!isShippingValid) {
                alert('Please fill in all required shipping fields.');
                return;
            }
            await fetchShippingRates();
            return;
        }

        if (!selectedShipping) {
            alert('Please select a shipping option.');
            return;
        }

        setProcessing(true);
        try {
            // Step 1: Create Razorpay order
            const res = await axios.post(`${API}/api/store/create-order`, {
                product_id: product.id,
                product_type: product.category,
                size: selectedSize,
                color: product.selected_color || '',
                quantity,
                shipping_address: shipping,
                shipping_cost: selectedShipping.buyer_pays_inr,
                shipping_method: selectedShipping.id
            }, { headers: { Authorization: `Bearer ${token}` } });

            const { razorpay_order_id, razorpay_key, amount, order_id } = res.data;

            // Step 2: Open Razorpay popup
            const options = {
                key: razorpay_key,
                amount: amount,
                currency: 'INR',
                name: 'ComicMenia Store',
                description: `${product.name}${selectedSize ? ` (${selectedSize})` : ''} × ${quantity}`,
                order_id: razorpay_order_id,
                handler: async function (response) {
                    // Step 3: Verify payment
                    try {
                        const verifyRes = await axios.post(`${API}/api/store/verify-payment`, {
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_signature: response.razorpay_signature
                        }, { headers: { Authorization: `Bearer ${token}` } });

                        if (verifyRes.data.success) {
                            setStep('details');
                            onClose();
                            alert('🎉 Order placed successfully! Check your notifications for updates.');
                        }
                    } catch (verifyErr) {
                        console.error('Verification error:', verifyErr);
                        alert('Payment received but verification failed. Contact support.');
                    }
                    setProcessing(false);
                },
                prefill: { name: shipping.name, contact: shipping.phone },
                theme: { color: '#7c3aed' },
                modal: {
                    ondismiss: function () { setProcessing(false); }
                }
            };

            const rzp = new window.Razorpay(options);
            rzp.open();
        } catch (err) {
            console.error('Order error:', err);
            alert(err?.response?.data?.detail || 'Failed to create order. Try again.');
            setProcessing(false);
        }
    };

    const currentImage = allImages[activeImageIndex] || allImages[0];
    const canPrev = activeImageIndex > 0;
    const canNext = activeImageIndex < allImages.length - 1;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

            {/* Modal */}
            <div
                className="relative w-full max-w-3xl bg-gray-900 border border-gray-700/50 rounded-2xl overflow-hidden shadow-2xl"
                onClick={(e) => e.stopPropagation()}
                style={{ maxHeight: '90vh', overflowY: 'auto' }}
            >
                {/* Close button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 z-10 p-2 rounded-full bg-gray-800/80 text-gray-400 hover:text-white hover:bg-gray-700 transition-all"
                >
                    <X className="w-5 h-5" />
                </button>

                <div className="md:flex">
                    {/* Image Gallery */}
                    <div className="md:w-1/2 relative">
                        {/* Main Image */}
                        <div className="relative h-80 bg-gray-800 overflow-hidden group">
                            {currentImage?.url ? (
                                <img
                                    src={currentImage.url}
                                    alt={currentImage.label || product.name}
                                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                                />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-700 to-gray-600">
                                    <span className="text-8xl">{CATEGORY_CONFIG[product.category]?.emoji || '🛍️'}</span>
                                </div>
                            )}

                            {/* Navigation Arrows */}
                            {allImages.length > 1 && (
                                <>
                                    {canPrev && (
                                        <button
                                            onClick={() => setActiveImageIndex(i => i - 1)}
                                            className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-all opacity-0 group-hover:opacity-100"
                                        >
                                            <ChevronLeft className="w-5 h-5" />
                                        </button>
                                    )}
                                    {canNext && (
                                        <button
                                            onClick={() => setActiveImageIndex(i => i + 1)}
                                            className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-all opacity-0 group-hover:opacity-100"
                                        >
                                            <ChevronRight className="w-5 h-5" />
                                        </button>
                                    )}
                                </>
                            )}

                            {/* Image Counter */}
                            {allImages.length > 1 && (
                                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-black/50 text-white text-xs font-medium backdrop-blur-sm">
                                    {activeImageIndex + 1} / {allImages.length}
                                    {currentImage?.label && <span className="ml-1 text-gray-300">• {currentImage.label}</span>}
                                </div>
                            )}
                        </div>

                        {/* Thumbnail Strip */}
                        {allImages.length > 1 && (
                            <div className="flex gap-1.5 p-3 overflow-x-auto bg-gray-900/80" style={{ scrollbarWidth: 'thin' }}>
                                {allImages.map((img, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setActiveImageIndex(i)}
                                        className={`flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 transition-all ${i === activeImageIndex
                                            ? 'border-purple-500 ring-1 ring-purple-400/40 scale-105'
                                            : 'border-gray-700 hover:border-gray-500 opacity-70 hover:opacity-100'
                                            }`}
                                    >
                                        {img.url ? (
                                            <img src={img.url} alt={img.label} className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full bg-gray-700 flex items-center justify-center text-lg">
                                                {CATEGORY_CONFIG[product.category]?.emoji || '🛍️'}
                                            </div>
                                        )}
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* Color Badge */}
                        {product.selected_color && (
                            <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-black/50 text-white text-xs font-medium backdrop-blur-sm flex items-center gap-1.5">
                                <span className="w-3 h-3 rounded-full border border-white/30" style={{ backgroundColor: product.selected_color === 'Black' ? '#222' : product.selected_color === 'White' ? '#eee' : '#666' }} />
                                {product.selected_color}
                            </div>
                        )}
                    </div>

                    {/* Details / Shipping */}
                    <div className="md:w-1/2 p-6 flex flex-col">
                        {/* Category badge */}
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-purple-400 bg-purple-500/10 px-3 py-1 rounded-full w-fit mb-3">
                            {CATEGORY_CONFIG[product.category]?.emoji} {CATEGORY_CONFIG[product.category]?.label || product.category}
                        </span>

                        <h2 className="text-2xl font-bold text-white mb-2">{product.name}</h2>

                        {step === 'details' ? (
                            <>
                                <p className="text-gray-400 text-sm mb-4 leading-relaxed">{product.description}</p>

                                {/* Price */}
                                <div className="flex items-baseline gap-2 mb-6">
                                    <span className="text-3xl font-black text-white">₹{product.price}</span>
                                    <span className="text-sm text-gray-500 line-through">₹{Math.round(product.price * 1.4)}</span>
                                    <span className="text-xs font-bold text-green-400 bg-green-500/10 px-2 py-0.5 rounded-full">30% OFF</span>
                                </div>

                                {/* Size selector */}
                                {sizeVariant && (
                                    <div className="mb-5">
                                        <label className="text-sm font-semibold text-gray-300 mb-2 block">Size</label>
                                        <div className="flex flex-wrap gap-2">
                                            {sizeVariant.options.map((size) => (
                                                <button
                                                    key={size}
                                                    onClick={() => setSelectedSize(size)}
                                                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${selectedSize === size
                                                        ? 'bg-purple-600 text-white ring-2 ring-purple-400/50'
                                                        : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
                                                        }`}
                                                >
                                                    {size}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Quantity */}
                                <div className="mb-6">
                                    <label className="text-sm font-semibold text-gray-300 mb-2 block">Quantity</label>
                                    <div className="flex items-center gap-3">
                                        <button
                                            onClick={() => setQuantity(q => Math.max(1, q - 1))}
                                            className="p-2 rounded-lg bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white transition-all"
                                        >
                                            <Minus className="w-4 h-4" />
                                        </button>
                                        <span className="text-white font-bold text-lg w-8 text-center">{quantity}</span>
                                        <button
                                            onClick={() => setQuantity(q => Math.min(10, q + 1))}
                                            className="p-2 rounded-lg bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white transition-all"
                                        >
                                            <Plus className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            </>
                        ) : step === 'shipping' ? (
                            /* Shipping Address Form */
                            <div className="space-y-3 mb-4">
                                <p className="text-sm text-gray-400 mb-2">📦 Shipping Address</p>
                                <select value={shipping.country_code}
                                    onChange={e => handleShippingChange('country_code', e.target.value)}
                                    className="w-full px-3 py-2.5 rounded-lg bg-gray-800 border border-gray-700 text-white text-sm focus:outline-none focus:border-purple-500 appearance-none cursor-pointer">
                                    <option value="IN">🇮🇳 India</option>
                                    <option value="US">🇺🇸 United States</option>
                                    <option value="GB">🇬🇧 United Kingdom</option>
                                    <option value="CA">🇨🇦 Canada</option>
                                    <option value="AU">🇦🇺 Australia</option>
                                    <option value="DE">🇩🇪 Germany</option>
                                    <option value="FR">🇫🇷 France</option>
                                    <option value="JP">🇯🇵 Japan</option>
                                    <option value="BR">🇧🇷 Brazil</option>
                                    <option value="MX">🇲🇽 Mexico</option>
                                    <option value="AE">🇦🇪 UAE</option>
                                    <option value="SG">🇸🇬 Singapore</option>
                                    <option value="NL">🇳🇱 Netherlands</option>
                                    <option value="IT">🇮🇹 Italy</option>
                                    <option value="ES">🇪🇸 Spain</option>
                                    <option value="SE">🇸🇪 Sweden</option>
                                    <option value="NZ">🇳🇿 New Zealand</option>
                                    <option value="KR">🇰🇷 South Korea</option>
                                    <option value="PH">🇵🇭 Philippines</option>
                                    <option value="MY">🇲🇾 Malaysia</option>
                                    <option value="ZA">🇿🇦 South Africa</option>
                                </select>
                                <input placeholder="Full Name *" value={shipping.name}
                                    onChange={e => handleShippingChange('name', e.target.value)}
                                    className="w-full px-3 py-2.5 rounded-lg bg-gray-800 border border-gray-700 text-white text-sm focus:outline-none focus:border-purple-500" />
                                <input placeholder="Address Line 1 *" value={shipping.address1}
                                    onChange={e => handleShippingChange('address1', e.target.value)}
                                    className="w-full px-3 py-2.5 rounded-lg bg-gray-800 border border-gray-700 text-white text-sm focus:outline-none focus:border-purple-500" />
                                <input placeholder="Address Line 2" value={shipping.address2}
                                    onChange={e => handleShippingChange('address2', e.target.value)}
                                    className="w-full px-3 py-2.5 rounded-lg bg-gray-800 border border-gray-700 text-white text-sm focus:outline-none focus:border-purple-500" />
                                <div className="grid grid-cols-2 gap-3">
                                    <input placeholder="City *" value={shipping.city}
                                        onChange={e => handleShippingChange('city', e.target.value)}
                                        className="px-3 py-2.5 rounded-lg bg-gray-800 border border-gray-700 text-white text-sm focus:outline-none focus:border-purple-500" />
                                    <input placeholder="State Code *" value={shipping.state_code}
                                        onChange={e => handleShippingChange('state_code', e.target.value)}
                                        className="px-3 py-2.5 rounded-lg bg-gray-800 border border-gray-700 text-white text-sm focus:outline-none focus:border-purple-500" />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <input placeholder="PIN Code *" value={shipping.zip}
                                        onChange={e => handleShippingChange('zip', e.target.value)}
                                        className="px-3 py-2.5 rounded-lg bg-gray-800 border border-gray-700 text-white text-sm focus:outline-none focus:border-purple-500" />
                                    <input placeholder="Phone *" value={shipping.phone}
                                        onChange={e => handleShippingChange('phone', e.target.value)}
                                        className="px-3 py-2.5 rounded-lg bg-gray-800 border border-gray-700 text-white text-sm focus:outline-none focus:border-purple-500" />
                                </div>
                                <button onClick={() => setStep('details')}
                                    className="text-xs text-purple-400 hover:underline mt-1">
                                    ← Back to product details
                                </button>
                            </div>
                        ) : step === 'rates' ? (
                            /* Shipping Options Selection */
                            <div className="space-y-3 mb-4">
                                <p className="text-sm text-gray-400 mb-2">🚚 Select Shipping Method</p>
                                {shippingOptions.map((opt) => (
                                    <button
                                        key={opt.id}
                                        onClick={() => setSelectedShipping(opt)}
                                        className={`w-full text-left p-3.5 rounded-xl border transition-all ${selectedShipping?.id === opt.id
                                            ? 'border-purple-500 bg-purple-500/10 ring-1 ring-purple-400/30'
                                            : 'border-gray-700 bg-gray-800/60 hover:border-gray-600'
                                            }`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2">
                                                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${selectedShipping?.id === opt.id
                                                        ? 'border-purple-500' : 'border-gray-600'}`}>
                                                        {selectedShipping?.id === opt.id && (
                                                            <div className="w-2 h-2 rounded-full bg-purple-500" />
                                                        )}
                                                    </div>
                                                    <span className="text-white text-sm font-semibold">{opt.name}</span>
                                                </div>
                                                {(opt.min_days || opt.max_days) && (
                                                    <p className="text-xs text-gray-500 mt-1 ml-6">
                                                        Est. delivery: {opt.min_days}–{opt.max_days} business days
                                                    </p>
                                                )}
                                            </div>
                                            <div className="text-right">
                                                {opt.buyer_pays_inr === 0 ? (
                                                    <span className="text-xs font-bold text-green-400 bg-green-400/10 px-2.5 py-1 rounded-full">
                                                        FREE
                                                    </span>
                                                ) : (
                                                    <span className="text-white font-bold text-sm">
                                                        +₹{opt.buyer_pays_inr}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </button>
                                ))}

                                {/* Price breakdown */}
                                {selectedShipping && (
                                    <div className="mt-3 p-3 rounded-lg bg-gray-800/50 border border-gray-700/50">
                                        <div className="flex justify-between text-xs text-gray-400">
                                            <span>Product ({quantity}×)</span>
                                            <span>₹{product.price * quantity}</span>
                                        </div>
                                        <div className="flex justify-between text-xs text-gray-400 mt-1">
                                            <span>Shipping</span>
                                            <span>{selectedShipping.buyer_pays_inr === 0
                                                ? <span className="text-green-400">FREE</span>
                                                : `₹${selectedShipping.buyer_pays_inr}`}</span>
                                        </div>
                                        <div className="flex justify-between text-sm text-white font-bold mt-2 pt-2 border-t border-gray-700">
                                            <span>Total</span>
                                            <span>₹{product.price * quantity + selectedShipping.buyer_pays_inr}</span>
                                        </div>
                                    </div>
                                )}

                                <button onClick={() => setStep('shipping')}
                                    className="text-xs text-purple-400 hover:underline mt-1">
                                    ← Change shipping address
                                </button>
                            </div>
                        ) : null}

                        {/* Buy / Checkout button */}
                        <button
                            onClick={handleBuyNow}
                            disabled={processing || loadingRates || (step === 'shipping' && !isShippingValid)}
                            className={`w-full py-3.5 rounded-xl font-bold text-base transition-all transform hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-purple-500/25 ${processing || loadingRates || (step === 'shipping' && !isShippingValid)
                                ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                                : 'bg-gradient-to-r from-purple-600 to-cyan-500 text-white hover:from-purple-500 hover:to-cyan-400'
                                }`}
                        >
                            {processing ? '⏳ Processing...'
                                : loadingRates ? '⏳ Calculating shipping...'
                                    : step === 'details' ? `Continue to Shipping — ₹${product.price * quantity}`
                                        : step === 'shipping' ? 'Calculate Shipping →'
                                            : step === 'rates' && selectedShipping
                                                ? `Pay ₹${product.price * quantity + selectedShipping.buyer_pays_inr} with Razorpay`
                                                : 'Select a shipping option'}
                        </button>

                        {/* Trust badges */}
                        <div className="mt-5 grid grid-cols-3 gap-2">
                            {[
                                { icon: Truck, label: 'Free Shipping', sub: 'Up to $5 covered' },
                                { icon: Shield, label: 'Secure Pay', sub: 'Razorpay' },
                                { icon: Package, label: 'Quality', sub: 'Premium Print' },
                            ].map(({ icon: Icon, label, sub }) => (
                                <div key={label} className="flex flex-col items-center text-center p-2 rounded-lg bg-gray-800/50">
                                    <Icon className="w-4 h-4 text-purple-400 mb-1" />
                                    <span className="text-[10px] font-semibold text-gray-300">{label}</span>
                                    <span className="text-[9px] text-gray-500">{sub}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* ═══ REVIEWS SECTION ═══ */}
                <ReviewsSection productId={product.id} />

            </div>
        </div>
    );
};

// Product Card
const ProductCard = ({ product, onClick }) => {
    const cat = CATEGORY_CONFIG[product.category];

    return (
        <div
            onClick={() => onClick(product)}
            className="group relative bg-gray-900/80 border border-gray-800/60 rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:border-purple-500/40 hover:shadow-xl hover:shadow-purple-500/10 hover:-translate-y-1"
        >
            {/* Image */}
            <div className="relative overflow-hidden">
                <ProductImage product={product} size="card" />
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                    <span className="opacity-0 group-hover:opacity-100 transition-all duration-300 bg-white/10 backdrop-blur-md text-white text-sm font-semibold px-4 py-2 rounded-full">
                        View Details →
                    </span>
                </div>
                {/* Featured badge */}
                {product.featured && (
                    <div className="absolute top-3 left-3 flex items-center gap-1 bg-yellow-500/90 text-black text-[10px] font-bold px-2.5 py-1 rounded-full">
                        <Star className="w-3 h-3" fill="currentColor" /> FEATURED
                    </div>
                )}
            </div>

            {/* Info */}
            <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                        <p className="text-xs text-purple-400 font-semibold mb-1">{cat?.emoji} {cat?.label}</p>
                        <h3 className="text-white font-bold text-sm truncate">{product.name}</h3>
                    </div>
                    <div className="text-right shrink-0">
                        <p className="text-white font-black text-lg">₹{product.price}</p>
                        <p className="text-gray-500 line-through text-xs">₹{Math.round(product.price * 1.4)}</p>
                    </div>
                </div>
                <p className="text-gray-500 text-xs mt-2 line-clamp-2">{product.description}</p>
                {/* Star rating on card */}
                {product.avg_rating > 0 && (
                    <div className="mt-1.5">
                        <StarRating rating={product.avg_rating} size="xs" count={product.review_count} />
                    </div>
                )}
                {/* Creator attribution */}
                {product.creator_name && (
                    <p className="text-xs mt-1.5">
                        <span className="text-gray-600">by </span>
                        {product.creator_username ? (
                            <Link
                                to={`/profile/${product.creator_username}`}
                                onClick={(e) => e.stopPropagation()}
                                className="text-purple-400 hover:text-purple-300 transition-colors hover:underline"
                            >
                                {product.creator_name}
                            </Link>
                        ) : (
                            <span className="text-gray-400">{product.creator_name}</span>
                        )}
                    </p>
                )}
            </div>
        </div>
    );
};

// Order Status Config
const ORDER_STATUSES = [
    { key: 'confirmed', label: 'Confirmed', icon: CheckCircle, color: 'text-yellow-400', bgDot: 'bg-yellow-400' },
    { key: 'in_production', label: 'In Production', icon: CircleDot, color: 'text-orange-400', bgDot: 'bg-orange-400' },
    { key: 'shipped', label: 'Shipped', icon: Package, color: 'text-blue-400', bgDot: 'bg-blue-400' },
    { key: 'in_transit', label: 'In Transit', icon: Truck, color: 'text-cyan-400', bgDot: 'bg-cyan-400' },
    { key: 'delivered', label: 'Delivered', icon: CheckCircle, color: 'text-green-400', bgDot: 'bg-green-400' },
    { key: 'canceled', label: 'Canceled', icon: XCircle, color: 'text-red-400', bgDot: 'bg-red-400' },
];

const getStatusIndex = (status) => {
    const idx = ORDER_STATUSES.findIndex(s => s.key === status);
    return idx >= 0 ? idx : 0;
};

// My Orders Section
const MyOrdersSection = ({ onClose }) => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [cancelingId, setCancelingId] = useState(null);
    const token = localStorage.getItem('token');

    useEffect(() => {
        const fetchOrders = async () => {
            if (!token) { setLoading(false); return; }
            try {
                const res = await axios.get(`${API}/api/store/my-orders`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setOrders(res.data?.orders || []);
            } catch (err) {
                console.error('Fetch orders error:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchOrders();

        // Poll every 30 seconds for real-time order status updates
        const pollInterval = setInterval(fetchOrders, 30000);
        return () => clearInterval(pollInterval);
    }, [token]);

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleDateString('en-IN', {
            day: 'numeric', month: 'short', year: 'numeric'
        });
    };

    const canCancel = (status) => ['confirmed', 'in_production', 'payment_pending'].includes(status);

    const handleCancelOrder = async (orderId, productName) => {
        if (!window.confirm(`Are you sure you want to cancel your order for "${productName}"? This action cannot be undone.`)) return;
        setCancelingId(orderId);
        try {
            await axios.post(`${API}/api/store/cancel-order/${orderId}`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: 'canceled' } : o));
            alert('Order canceled successfully.');
        } catch (err) {
            alert(err.response?.data?.detail || 'Failed to cancel order. Please try again.');
        } finally {
            setCancelingId(null);
        }
    };

    if (!token) {
        return (
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
                <Package className="w-16 h-16 mx-auto text-gray-600 mb-4" />
                <h2 className="text-xl font-bold text-white mb-2">Sign in to view orders</h2>
                <p className="text-gray-400">Log in to see your order history and tracking.</p>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                    <button onClick={onClose} className="p-2 rounded-lg bg-gray-800 text-gray-400 hover:text-white transition-all">
                        <ChevronLeft className="w-5 h-5" />
                    </button>
                    <div>
                        <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                            <Package className="w-6 h-6 text-purple-400" />
                            My Orders
                        </h2>
                        <p className="text-sm text-gray-500">{orders.length} order{orders.length !== 1 ? 's' : ''}</p>
                    </div>
                </div>
            </div>

            {/* Orders List */}
            {loading ? (
                <div className="space-y-4">
                    {[1, 2].map(i => (
                        <div key={i} className="bg-gray-900/60 border border-gray-800/50 rounded-2xl p-6 animate-pulse">
                            <div className="flex gap-4">
                                <div className="w-20 h-20 bg-gray-800 rounded-xl" />
                                <div className="flex-1 space-y-3">
                                    <div className="h-4 bg-gray-800 rounded w-1/3" />
                                    <div className="h-3 bg-gray-800 rounded w-1/4" />
                                    <div className="h-3 bg-gray-800 rounded w-full" />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : orders.length === 0 ? (
                <div className="text-center py-20">
                    <div className="text-6xl mb-4">📦</div>
                    <h3 className="text-xl font-bold text-white mb-2">No orders yet</h3>
                    <p className="text-gray-400 mb-6">Your order history will appear here after your first purchase.</p>
                    <button onClick={onClose} className="px-6 py-2.5 rounded-xl bg-purple-600 text-white font-semibold hover:bg-purple-500 transition-all">
                        Browse Store
                    </button>
                </div>
            ) : (
                <div className="space-y-5">
                    {orders.map(order => {
                        const statusIdx = getStatusIndex(order.status || 'confirmed');
                        const currentStatus = ORDER_STATUSES[statusIdx];
                        const StatusIcon = currentStatus.icon;

                        return (
                            <div key={order.id} className="bg-gray-900/60 border border-gray-800/50 rounded-2xl overflow-hidden hover:border-gray-700/60 transition-all">
                                {/* Order Header */}
                                <div className="px-5 py-3 bg-gray-800/30 border-b border-gray-800/50 flex items-center justify-between flex-wrap gap-2">
                                    <div className="flex items-center gap-3">
                                        <span className="text-xs text-gray-500 font-mono">#{order.id?.slice(0, 8)}</span>
                                        <span className="text-xs text-gray-600">•</span>
                                        <span className="text-xs text-gray-400 flex items-center gap-1">
                                            <Clock className="w-3 h-3" />
                                            {formatDate(order.created_at)}
                                        </span>
                                    </div>
                                    <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${currentStatus.color} bg-gray-800`}>
                                        <StatusIcon className="w-3.5 h-3.5" />
                                        {currentStatus.label}
                                    </div>
                                </div>

                                {/* Order Body */}
                                <div className="p-5">
                                    <div className="flex gap-4">
                                        {/* Product Image */}
                                        <div className="w-20 h-20 rounded-xl overflow-hidden bg-gray-800 flex-shrink-0">
                                            {order.product_image ? (
                                                <img src={order.product_image} alt={order.product_name} className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-3xl">
                                                    {CATEGORY_CONFIG[order.product_type]?.emoji || '🛍️'}
                                                </div>
                                            )}
                                        </div>

                                        {/* Product Info */}
                                        <div className="flex-1 min-w-0">
                                            <h3 className="text-white font-bold text-base mb-0.5">{order.product_name || 'Product'}</h3>
                                            <p className="text-gray-500 text-xs mb-2">
                                                {order.variant_size && <span>{order.variant_size}</span>}
                                                {order.quantity > 1 && <span> × {order.quantity}</span>}
                                            </p>
                                            <p className="text-white font-bold text-lg">₹{order.total_amount || order.amount || 0}</p>
                                        </div>
                                    </div>

                                    {/* Status Timeline */}
                                    <div className="mt-5 flex items-center gap-0">
                                        {ORDER_STATUSES.map((s, i) => {
                                            const reached = i <= statusIdx;
                                            const isLast = i === ORDER_STATUSES.length - 1;
                                            return (
                                                <React.Fragment key={s.key}>
                                                    <div className="flex flex-col items-center" style={{ minWidth: '20px' }}>
                                                        <div className={`w-3 h-3 rounded-full border-2 transition-all ${reached
                                                            ? `${s.bgDot} border-transparent`
                                                            : 'bg-gray-800 border-gray-700'
                                                            }`} />
                                                        <span className={`text-[9px] mt-1 whitespace-nowrap ${reached ? 'text-gray-300 font-medium' : 'text-gray-600'
                                                            }`}>
                                                            {s.label}
                                                        </span>
                                                    </div>
                                                    {!isLast && (
                                                        <div className={`flex-1 h-0.5 mb-4 transition-all ${i < statusIdx ? 'bg-gradient-to-r from-purple-500 to-cyan-500' : 'bg-gray-800'
                                                            }`} />
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </div>

                                    {/* Tracking Info */}
                                    {(order.tracking_number || order.tracking_url) && (
                                        <div className="mt-4 p-3 rounded-xl bg-gray-800/50 border border-gray-700/40">
                                            <div className="flex items-center justify-between flex-wrap gap-2">
                                                <div className="flex items-center gap-2">
                                                    <Truck className="w-4 h-4 text-cyan-400" />
                                                    <span className="text-sm text-gray-300">
                                                        {order.carrier && <span className="font-semibold text-white">{order.carrier} </span>}
                                                        {order.tracking_number && (
                                                            <span className="font-mono text-cyan-300">{order.tracking_number}</span>
                                                        )}
                                                    </span>
                                                </div>
                                                {order.tracking_url && (
                                                    <a
                                                        href={order.tracking_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyan-500/10 text-cyan-400 text-xs font-semibold hover:bg-cyan-500/20 transition-all"
                                                    >
                                                        <ExternalLink className="w-3.5 h-3.5" />
                                                        Track Package
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Cancel Button */}
                                    {canCancel(order.status) && (
                                        <div className="mt-4 flex justify-end">
                                            <button
                                                onClick={() => handleCancelOrder(order.id, order.product_name)}
                                                disabled={cancelingId === order.id}
                                                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-500/10 text-red-400 text-sm font-semibold hover:bg-red-500/20 border border-red-500/20 hover:border-red-500/40 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <XCircle className="w-4 h-4" />
                                                {cancelingId === order.id ? 'Canceling...' : 'Cancel Order'}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// Main Store Page
const Store = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeCategory, setActiveCategory] = useState('all');
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [showOrders, setShowOrders] = useState(false);
    const location = useLocation();

    // Auto-open orders if ?orders=1 is in URL (from notification click)
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('orders') === '1' && localStorage.getItem('token')) {
            setShowOrders(true);
        }
    }, [location.search]);

    const fetchProducts = useCallback(async () => {
        setLoading(true);
        try {
            const params = activeCategory !== 'all' ? { category: activeCategory } : {};
            const res = await axios.get(`${API}/api/store/products`, { params });
            const fetched = res.data?.products || [];
            setProducts(fetched);
        } catch (err) {
            console.error('Store fetch error:', err);
            setProducts([]);
        } finally {
            setLoading(false);
        }
    }, [activeCategory]);

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    const filtered = activeCategory === 'all'
        ? products
        : products.filter(p => p.category === activeCategory);

    return (
        <div className="min-h-screen bg-gray-950">
            {/* Hero */}
            <div className="relative overflow-hidden">
                {/* Animated gradient background */}
                <div className="absolute inset-0 bg-gradient-to-br from-purple-900/40 via-gray-950 to-cyan-900/30" />
                <div className="absolute inset-0" style={{
                    backgroundImage: `radial-gradient(ellipse at 20% 50%, rgba(139,92,246,0.15) 0%, transparent 50%),
                           radial-gradient(ellipse at 80% 20%, rgba(6,182,212,0.1) 0%, transparent 50%)`,
                }} />

                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-8">
                    <div className="text-center">
                        <div className="inline-flex items-center gap-2 bg-purple-500/10 border border-purple-500/20 text-purple-300 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
                            <ShoppingBag className="w-4 h-4" />
                            Official ComicMenia Merch
                        </div>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-3 tracking-tight">
                            Wear Your <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent">Comics</span>
                        </h1>
                        <p className="text-gray-400 text-base md:text-lg max-w-xl mx-auto">
                            Premium merch featuring the best comic art. Mugs, tees, posters & more.
                        </p>

                        {/* My Orders Button */}
                        {localStorage.getItem('token') && (
                            <button
                                onClick={() => setShowOrders(!showOrders)}
                                className={`mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${showOrders
                                    ? 'bg-purple-600 text-white shadow-lg shadow-purple-500/25'
                                    : 'bg-gray-800/60 text-gray-300 border border-gray-700/40 hover:bg-gray-700/80 hover:text-white'
                                    }`}
                            >
                                <Package className="w-4 h-4" />
                                {showOrders ? 'Back to Store' : 'My Orders'}
                            </button>
                        )}
                    </div>

                    {/* Category Tabs */}
                    <div className="mt-8 flex flex-wrap justify-center gap-2">
                        {Object.entries(CATEGORY_CONFIG).map(([key, { label, emoji }]) => (
                            <button
                                key={key}
                                onClick={() => setActiveCategory(key)}
                                className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 ${activeCategory === key
                                    ? 'bg-purple-600 text-white shadow-lg shadow-purple-500/25'
                                    : 'bg-gray-800/60 text-gray-400 hover:bg-gray-700/80 hover:text-white border border-gray-700/40'
                                    }`}
                            >
                                <span className="mr-1.5">{emoji}</span>
                                {label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Product Grid or My Orders */}
            {showOrders ? (
                <MyOrdersSection onClose={() => setShowOrders(false)} />
            ) : (
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    {loading ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {Array.from({ length: 8 }).map((_, i) => (
                                <div key={i} className="bg-gray-900/50 border border-gray-800/40 rounded-2xl overflow-hidden animate-pulse">
                                    <div className="h-56 bg-gray-800/50" />
                                    <div className="p-4 space-y-3">
                                        <div className="h-3 bg-gray-800 rounded w-1/3" />
                                        <div className="h-4 bg-gray-800 rounded w-2/3" />
                                        <div className="h-3 bg-gray-800 rounded w-full" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="text-center py-20">
                            <div className="text-6xl mb-4">🔍</div>
                            <h3 className="text-xl font-bold text-white mb-2">No products yet</h3>
                            <p className="text-gray-400">Check back soon — we're adding more merch!</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {filtered.map((product) => (
                                <ProductCard
                                    key={product.id}
                                    product={product}
                                    onClick={setSelectedProduct}
                                />
                            ))}
                        </div>
                    )}

                    {/* Bottom info bar */}
                    <div className="mt-16 border-t border-gray-800 pt-8">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                            {[
                                { icon: Truck, title: 'Free Shipping', desc: 'On orders above ₹500' },
                                { icon: Shield, title: 'Secure Payments', desc: 'Powered by Razorpay' },
                                { icon: Package, title: 'Premium Quality', desc: 'Print-on-demand by Printful' },
                            ].map(({ icon: Icon, title, desc }) => (
                                <div key={title} className="flex flex-col items-center">
                                    <div className="p-3 rounded-xl bg-purple-500/10 mb-3">
                                        <Icon className="w-6 h-6 text-purple-400" />
                                    </div>
                                    <h4 className="text-white font-bold text-sm">{title}</h4>
                                    <p className="text-gray-500 text-xs mt-1">{desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Product Detail Modal */}
            {selectedProduct && (
                <ProductModal product={selectedProduct} onClose={() => setSelectedProduct(null)} />
            )}
        </div>
    );
};

export default Store;
